import { Autocomplete, createFilterOptions } from '@mui/material';
import { AppTextInput } from 'components/FormElements';
import { forwardRef, useState } from 'react';

type Props = {
  error?: {
    message?: string;
  };
  placeholder?: string;
  value?: number | null;
  onChange: (value: number) => void;
  name?: string;
  disabled?: boolean;
};

const VatRateSelector = forwardRef<HTMLDivElement, Props>(
  ({ name, disabled, error, placeholder, value, onChange }, ref) => {
    const filter = createFilterOptions<string>();
    const [inputValue, setInputValue] = useState('');

    // Derive the display value directly from the value prop
    const displayValue =
      value !== undefined && value !== null ? `${value}%` : '';

    const formatValueWithPercent = (val: string) => {
      if (!val) return '';
      const cleanValue = val.replace('%', '');
      return `${cleanValue}%`;
    };

    const handleChange = (_, newValue: string | number) => {
      if (typeof newValue === 'number') {
        onChange(newValue);
        return;
      }
      const cleanValue = newValue.replace('%', '');
      const newValueNumber = Number(
        cleanValue.startsWith('Use ') ? cleanValue.split(' ')[1] : cleanValue,
      );
      // if isNaN, set to 0
      if (isNaN(newValueNumber)) {
        onChange(0);
        return;
      }
      onChange(newValueNumber);
    };

    return (
      <Autocomplete
        freeSolo
        disableClearable
        disabled={disabled}
        ref={ref}
        options={['0', '20']}
        inputValue={inputValue || displayValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <AppTextInput
            {...params}
            name={name}
            error={!!error}
            helperText={error?.message?.toString()}
            type="text"
            placeholder={placeholder}
            onKeyDown={(event) => {
              // check event.key is not a letter, space, or special character
              const isLetter = /^[a-zA-Z]$/.test(event.key);
              const isSpace = event.key === ' ';
              const isSpecialCharacter =
                /[!@#$%^&*()_+\-=[\]{};':"\\|,<>/?]/.test(event.key);

              if (isLetter || isSpace || isSpecialCharacter) {
                event.preventDefault();
              }
            }}
            onBlur={() => {
              const currentValue = inputValue || displayValue;
              if (currentValue && !currentValue.includes('%')) {
                const formattedValue = formatValueWithPercent(currentValue);
                setInputValue(formattedValue);
                handleChange(null, formattedValue);
              } else {
                // Reset inputValue to allow the derived displayValue to show
                setInputValue('');
              }
            }}
          />
        )}
        value={value?.toString() ?? ''}
        getOptionLabel={(option) =>
          option || option === '0' ? `${option}%` : ''
        }
        getOptionKey={(option) => option}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const trimmedInputValue = inputValue.trim().replace('%', '').trim();

          const isNumber = !isNaN(Number(trimmedInputValue));
          // Suggest the creation of a new value
          const isExisting = options.some(
            (option) => trimmedInputValue === option,
          );
          if (trimmedInputValue !== '' && !isExisting && isNumber) {
            filtered.push(`Use ${trimmedInputValue}`);
          }

          return filtered;
        }}
        onChange={handleChange}
      />
    );
  },
);

export default VatRateSelector;
